import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail, Star, AlertCircle, Clock, Inbox, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/shared/EmptyState';
import { cn } from '@/lib/utils';
import { getMicrosoftAuthUrl } from '@/lib/oauth';
import { EmailReadingPanel } from '@/features/email/components/EmailReadingPanel';
import { EmailComposeDialog } from '@/features/email/components/EmailComposeDialog';

interface EmailMessage {
  id: string;
  provider: 'microsoft' | 'google';
  subject: string;
  from: string;
  from_email: string;
  preview: string;
  date: string;
  is_read: boolean;
  has_attachments: boolean;
  account: string;
}

type SmartFolder = 'inbox' | 'starred' | 'needs_followup' | 'action_required';

const SMART_FOLDERS = [
  { id: 'inbox' as const, label: 'Inbox', icon: Inbox },
  { id: 'starred' as const, label: 'Starred', icon: Star },
  { id: 'needs_followup' as const, label: 'Needs Follow-up', icon: Clock },
  { id: 'action_required' as const, label: 'Action Required', icon: AlertCircle },
];

export function EmailPage() {
  const [selectedFolder, setSelectedFolder] = useState<SmartFolder>('inbox');
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['emails', selectedFolder],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('email-inbox', {
        method: 'GET',
      });
      if (error) throw error;
      return data as { emails: EmailMessage[] };
    },
  });

  const emails = data?.emails ?? [];

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col">
      <PageHeader
        title="Email"
        description="Unified inbox across all connected accounts"
        action={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { window.location.href = getMicrosoftAuthUrl(); }}
            >
              Connect Outlook
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            <Button size="sm" onClick={() => setComposeOpen(true)}>
              <Mail className="mr-2 h-4 w-4" /> Compose
            </Button>
          </div>
        }
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Smart Folders */}
        <div className="w-48 shrink-0 border-r p-2">
          {SMART_FOLDERS.map((folder) => (
            <button
              key={folder.id}
              onClick={() => setSelectedFolder(folder.id)}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
                selectedFolder === folder.id
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-muted-foreground hover:bg-accent',
              )}
            >
              <folder.icon className="h-4 w-4" />
              {folder.label}
            </button>
          ))}
        </div>

        {/* Email List */}
        <div className="w-96 shrink-0 overflow-y-auto border-r">
          {isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : emails.length === 0 ? (
            <EmptyState
              icon={Mail}
              title="No emails"
              description="Connect your email account in Settings"
            />
          ) : (
            emails.map((email) => (
              <button
                key={`${email.provider}-${email.id}`}
                onClick={() => setSelectedEmail(email)}
                className={cn(
                  'w-full border-b p-3 text-left hover:bg-accent/50',
                  !email.is_read && 'bg-primary/5',
                  selectedEmail?.id === email.id && 'bg-accent',
                )}
              >
                <div className="flex items-start justify-between">
                  <span className={cn('text-sm', !email.is_read && 'font-semibold')}>
                    {email.from}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(email.date).toLocaleDateString()}
                  </span>
                </div>
                <p className={cn('text-sm', !email.is_read && 'font-medium')}>
                  {email.subject}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground line-clamp-1">
                  {email.preview}
                </p>
                <div className="mt-1 flex items-center gap-1">
                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                    {email.provider === 'microsoft' ? 'Outlook' : 'Gmail'}
                  </Badge>
                  {email.has_attachments && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                      📎
                    </Badge>
                  )}
                </div>
              </button>
            ))
          )}
        </div>

        {/* Reading Panel */}
        <div className="flex-1 overflow-y-auto">
          {selectedEmail ? (
            <EmailReadingPanel email={selectedEmail} />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              Select an email to read
            </div>
          )}
        </div>
      </div>

      <EmailComposeDialog open={composeOpen} onOpenChange={setComposeOpen} />
    </div>
  );
}
